'use client';

import { faHeart } from '@fortawesome/pro-light-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';

import { entityActionStyle } from './entity-action.styles';

import { Icon } from '@/components/ui/icon.component';
import { authStore } from '@/store/auth.store';
import { setOpenSignInModal } from '@/store/modal';
import { useFavorite } from '@/hooks/use-favorite.hook';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { FavoriteOrigin } from '@/types/enums/analytics/favorites/favorites-analytics.enum';
import { LoginOriginEnum } from '@/types/enums/analytics/login/login-analytics.enum';

interface EntityFavoriteButtonProps {
  entityId: string;
  entityType: EntityTypeEnum;
  variant?: 'icon' | 'labeled';
}

export function EntityFavoriteButton({ entityId, entityType, variant = 'icon' }: EntityFavoriteButtonProps) {
  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);
  const isAuthReady = useStore(authStore, (state) => state.computed.isAuthReady);
  const { addFavorite, removeFavorite, checkFavoriteStatus } = useFavorite();

  const [isFavorite, setIsFavorite] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }

    if (!isLoggedIn) {
      setIsFavorite(false);
      return;
    }

    let isMounted = true;

    checkFavoriteStatus({ type: entityType, id: entityId }).then((status) => {
      if (isMounted) {
        setIsFavorite(Boolean(status));
      }
    });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady, isLoggedIn, entityId, entityType]);

  async function toggleFavorite() {
    setIsSubmitting(true);

    try {
      const wasFavorite = isFavorite;
      const action = wasFavorite ? removeFavorite : addFavorite;
      const succeeded = await action({ type: entityType, id: entityId, origem: FavoriteOrigin.PERFIL });

      if (succeeded) {
        setIsFavorite(!wasFavorite);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClick() {
    if (!isLoggedIn) {
      setOpenSignInModal(true, { origin: LoginOriginEnum.ENTITY, loginCallback: toggleFavorite });
      return;
    }

    toggleFavorite();
  }

  return (
    <button
      type="button"
      data-testid={DataTestIdEnum.ENTITY_FAVORITE_BUTTON}
      aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
      aria-pressed={isFavorite}
      className={entityActionStyle({ variant })}
      onClick={handleClick}
      disabled={isSubmitting || !isAuthReady}
    >
      <Icon icon={isFavorite ? faHeartSolid : faHeart} className="w-4 h-4 text-green-500" />
      {variant === 'labeled' && 'Favoritar'}
    </button>
  );
}
